import { msg } from "@lingui/core/macro";
import {
  createEmployeeMutation,
  deleteEmployeeMutation,
  type EmployeeRole,
  getEmployeesQuery,
  queryKeys,
} from "@comandero/shared";
import type { Branch, Employee, Organization } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createSignal, For } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface StaffManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

const ROLES: EmployeeRole[] = ["waiter", "cashier", "manager"];
function toRole(value: string): EmployeeRole {
  return ROLES.find((r) => r === value) ?? "waiter";
}

export function StaffManager(props: StaffManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [name, setName] = createSignal("");
  const [pin, setPin] = createSignal("");
  const [role, setRole] = createSignal<EmployeeRole>("waiter");

  const orgId = (): string | null => props.org()?.id ?? null;

  const employees = createPowerSyncQuery<Employee>({
    queryKey: () => queryKeys.comandero.employees(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getEmployeesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  async function addEmployee(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !name().trim()) {
      return;
    }
    await createEmployeeMutation(d, id, name(), {
      pin: pin() || undefined,
      role: role(),
      branchId: props.branch()?.id ?? null,
    });
    setName("");
    setPin("");
  }

  async function removeEmployee(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteEmployeeMutation(d, id);
  }

  return (
    <div class="flex flex-col gap-6">
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Employees`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addEmployee}>
          <TextField
            data-testid="employee-name-input"
            placeholder={t(msg`Employee name`)}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <TextField
            data-testid="employee-pin-input"
            placeholder={t(msg`PIN`)}
            inputmode="numeric"
            value={pin()}
            onInput={(e) => setPin(e.currentTarget.value)}
          />
          <Select
            data-testid="employee-role-select"
            aria-label={t(msg`Role`)}
            value={role()}
            onChange={(v) => setRole(toRole(v))}
            options={[
              { value: "waiter", label: t(msg`Waiter`) },
              { value: "cashier", label: t(msg`Cashier`) },
              { value: "manager", label: t(msg`Manager`) },
            ]}
          />
          <Button data-testid="add-employee-btn" type="submit">
            {t(msg`Add employee`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={employees.data}>
            {(emp) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="employee-item"
                data-employee-id={emp.id}
              >
                <span data-testid="employee-row-name">{emp.name}</span>
                <span class="text-muted-foreground" data-testid="employee-row-role">
                  {emp.role}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="delete-employee-btn"
                  onClick={() => void removeEmployee(emp.id)}
                >
                  {t(msg`Delete`)}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </div>
  );
}
